"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultRedeemAPI = exports.encodeRedeemRequest = void 0;
const big_js_1 = __importDefault(require("big.js"));
const vaults_1 = require("./vaults");
const utils_1 = require("../utils");
const issueRedeem_1 = require("../utils/issueRedeem");
const collateral_1 = require("./collateral");
const transaction_1 = require("./transaction");
const oracle_1 = require("./oracle");
function encodeRedeemRequest(req, network) {
    return utils_1.encodeParachainRequest(req, network);
}
exports.encodeRedeemRequest = encodeRedeemRequest;
class DefaultRedeemAPI extends transaction_1.DefaultTransactionAPI {
    constructor(api, btcNetwork, electrsAPI, account) {
        super(api, account);
        this.btcNetwork = btcNetwork;
        this.electrsAPI = electrsAPI;
        this.requestHash = this.api.createType("Hash");
        this.events = [];
        this.vaultsAPI = new vaults_1.DefaultVaultsAPI(api, btcNetwork, account);
        this.collateralAPI = new collateral_1.DefaultCollateralAPI(api, account);
        this.oracleAPI = new oracle_1.DefaultOracleAPI(api, account);
    }
    getRedeemIdsFromEvents(events) {
        return issueRedeem_1.getRequestIdsFromEvents(events, this.api.events.redeem.RequestRedeem, this.api);
    }
    request(amount, btcAddressEnc, atomic = true, retries = 0, cachedVaults) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const availableVaults = cachedVaults || (yield this.vaultsAPI.getVaultsWithRedeemableTokens());
                const amountsPerVault = issueRedeem_1.allocateAmountsToVaults(availableVaults, amount);
                const result = yield this.requestAdvanced(amountsPerVault, btcAddressEnc, atomic);
                const successfulSum = result.reduce((sum, req) => sum.plus(req.redeemRequest.amount_btc.toString()), new big_js_1.default(0));
                const remainder = amount.sub(successfulSum);
                if (remainder.eq(0) || retries === 0)
                    return result;
                else {
                    return (yield this.request(remainder, btcAddressEnc, atomic, retries - 1, availableVaults)).concat(result);
                }
            }
            catch (e) {
                return Promise.reject(e.message);
            }
        });
    }
    requestAdvanced(amountsPerVault, btcAddressEnc, atomic) {
        return __awaiter(this, void 0, void 0, function* () {
            const btcAddress = this.api.createType("BtcAddress", utils_1.decodeBtcAddress(btcAddressEnc, this.btcNetwork));
            const txes = new Array();
            for (const [vault, amount] of amountsPerVault) {
                const amountWrapped = this.api.createType("Compact<Wrapped>", utils_1.btcToSat(amount.toString()));
                txes.push(this.api.tx.redeem.requestRedeem(amountWrapped, btcAddress, vault));
            }
            // batchAll fails atomically, batch allows partial successes
            const batch = (atomic ? this.api.tx.utility.batchAll : this.api.tx.utility.batch)(txes);
            try {
                const result = yield this.sendLogged(batch, this.api.events.issue.RequestRedeem);
                const ids = this.getRedeemIdsFromEvents(result.events);
                const redeemRequests = yield this.getRequestsById(ids);
                return ids.map((id, idx) => ({ id, redeemRequest: redeemRequests[idx] }));
            }
            catch (e) {
                return Promise.reject(e);
            }
        });
    }
    execute(requestId, btcTxId, merkleProof, rawTx) {
        return __awaiter(this, void 0, void 0, function* () {
            const parsedRequestId = this.api.createType("H256", requestId);
            [merkleProof, rawTx] = yield utils_1.getTxProof(this.electrsAPI, btcTxId, merkleProof, rawTx);
            const executeRedeemTx = this.api.tx.redeem.executeRedeem(parsedRequestId, merkleProof, rawTx);
            const result = yield this.sendLogged(executeRedeemTx, this.api.events.redeem.ExecuteRedeem);
            const ids = this.getRedeemIdsFromEvents(result.events);
            if (ids.length > 1) {
                throw new Error("Unexpected multiple redeem events from single execute transaction!");
            }
            else if (ids.length === 1) {
                return true;
            }
            return false;
        });
    }
    cancel(redeemId, reimburse) {
        return __awaiter(this, void 0, void 0, function* () {
            const reimburseValue = reimburse ? reimburse : false;
            const cancelRedeemTx = this.api.tx.redeem.cancelRedeem(redeemId, reimburseValue);
            yield this.sendLogged(cancelRedeemTx, this.api.events.redeem.CancelRedeem);
        });
    }
    burn(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const amountSat = this.api.createType("Balance", utils_1.btcToSat(amount.toString()));
            const burnRedeemTx = this.api.tx.redeem.liquidationRedeem(amountSat);
            yield this.sendLogged(burnRedeemTx, this.api.events.redeem.LiquidationRedeem);
        });
    }
    setRedeemPeriod(blocks) {
        return __awaiter(this, void 0, void 0, function* () {
            const period = this.api.createType("BlockNumber", blocks);
            const tx = this.api.tx.sudo
                .sudo(this.api.tx.redeem.setRedeemPeriod(period));
            yield this.sendLogged(tx);
        });
    }
    getRedeemPeriod() {
        return __awaiter(this, void 0, void 0, function* () {
            const blockNumber = yield this.api.query.redeem.redeemPeriod();
            return blockNumber.toNumber();
        });
    }
    getMaxBurnableTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const liquidationVault = yield this.vaultsAPI.getLiquidationVault();
            return new big_js_1.default(utils_1.satToBTC(liquidationVault.issued_tokens.toString()));
        });
    }
    getBurnExchangeRate() {
        return __awaiter(this, void 0, void 0, function* () {
            const liquidationVault = yield this.vaultsAPI.getLiquidationVault();
            const wrappedSatoshi = liquidationVault.issued_tokens.add(liquidationVault.to_be_issued_tokens);
            if (wrappedSatoshi.isZero()) {
                return Promise.reject("There are no burnable tokens. The burn exchange rate is undefined");
            }
            const wrappedBtc = new big_js_1.default(utils_1.satToBTC(wrappedSatoshi.toString()));
            const liquidationVaultId = yield this.vaultsAPI.getLiquidationVaultId();
            const collateralDot = yield this.collateralAPI.balanceLocked(this.api.createType("AccountId", liquidationVaultId));
            return collateralDot.div(wrappedBtc);
        });
    }
    getCurrentInclusionFee() {
        return __awaiter(this, void 0, void 0, function* () {
            const head = yield this.api.rpc.chain.getFinalizedHead();
            const [size, satoshiFees] = yield Promise.all([
                this.api.query.redeem.redeemTransactionSize.at(head),
                this.oracleAPI.getBtcTxFeesPerByte()
            ]);
            const btcFees = new big_js_1.default(utils_1.satToBTC(satoshiFees.fast.toString()));
            return btcFees.mul(new big_js_1.default(size.toString()));
        });
    }
    list() {
        return __awaiter(this, void 0, void 0, function* () {
            const head = yield this.api.rpc.chain.getFinalizedHead();
            const redeemRequests = yield this.api.query.redeem.redeemRequests.entriesAt(head);
            return redeemRequests.map((v) => encodeRedeemRequest(v[1], this.btcNetwork));
        });
    }
    mapForUser(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const redeemRequestPairs = yield this.api.rpc.redeem.getRedeemRequests(account);
            const mapForUser = new Map();
            redeemRequestPairs.forEach((redeemRequestPair) => mapForUser.set(redeemRequestPair[0], encodeRedeemRequest(redeemRequestPair[1], this.btcNetwork)));
            return mapForUser;
        });
    }
    subscribeToRedeemExpiry(account, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const redeemPeriod = this.api.createType("BlockNumber", yield this.getRedeemPeriod());
            const unsubscribe = this.onRedeem(account, (seen, request, id, currentBlockNumber) => {
                if (request.opentime.add(redeemPeriod).lte(currentBlockNumber)
                    && !seen.has(id)
                    && request.status.isPending) {
                    seen.add(id);
                    callback(id);
                }
            });
            return unsubscribe;
        });
    }
    subscribeToRedeemCompletion(account, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const unsubscribe = this.onRedeem(account, (seen, request, id, _currentBlockNumber) => {
                if (request.status.isCompleted
                    && !seen.has(id)) {
                    seen.add(id);
                    callback(id);
                }
            });
            return unsubscribe;
        });
    }
    onRedeem(account, fn) {
        return __awaiter(this, void 0, void 0, function* () {
            const seen = new Set();
            try {
                const unsubscribe = yield this.api.rpc.chain.subscribeFinalizedHeads((header) => __awaiter(this, void 0, void 0, function* () {
                    const redeemRequests = yield this.mapForUser(account);
                    redeemRequests.forEach((request, id) => {
                        fn(seen, request, id, header.number.toBn());
                    });
                }));
                return unsubscribe;
            }
            catch (error) {
                console.log(`Error onRedeem: ${error}`);
            }
            // as a fallback, return an empty void function
            return () => {
                return;
            };
        });
    }
    getFeesToPay(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const feePercentage = yield this.getFeeRate();
            return amount.mul(feePercentage);
        });
    }
    getFeeRate() {
        return __awaiter(this, void 0, void 0, function* () {
            const head = yield this.api.rpc.chain.getFinalizedHead();
            const redeemFee = yield this.api.query.fee.redeemFee.at(head);
            return new big_js_1.default(utils_1.decodeFixedPointType(redeemFee));
        });
    }
    getDustValue() {
        return __awaiter(this, void 0, void 0, function* () {
            const head = yield this.api.rpc.chain.getFinalizedHead();
            const dustValueSat = yield this.api.query.redeem.redeemBtcDustValue.at(head);
            return new big_js_1.default(utils_1.satToBTC(dustValueSat.toString()));
        });
    }
    getPremiumRedeemFee() {
        return __awaiter(this, void 0, void 0, function* () {
            const head = yield this.api.rpc.chain.getFinalizedHead();
            const premiumRedeemFee = yield this.api.query.fee.premiumRedeemFee.at(head);
            return utils_1.decodeFixedPointType(premiumRedeemFee);
        });
    }
    getRequestById(redeemId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.getRequestsById([redeemId]))[0];
        });
    }
    getRequestsById(redeemIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const head = yield this.api.rpc.chain.getFinalizedHead();
            return Promise.all(redeemIds.map((redeemId) => __awaiter(this, void 0, void 0, function* () { return encodeRedeemRequest(yield this.api.query.redeem.redeemRequests.at(head, redeemId), this.btcNetwork); })));
        });
    }
}
exports.DefaultRedeemAPI = DefaultRedeemAPI;
//# sourceMappingURL=redeem.js.map